import type { Point } from '@/types';

/**
 * Demo shapes for demonstrating boundary functionality.
 * Shapes are generated relative to a center point and scaled to fit viewport.
 */

/**
 * Generate a smooth blob/organic shape around the center.
 * Uses a perturbed circle with varying radii at different angles.
 */
export function generateBlobShape(
  center: Point,
  baseRadius: number,
  numPoints: number = 64
): Point[] {
  const points: Point[] = [];
  
  // Pre-defined radius variations for a consistent demo shape
  // These create an interesting organic blob that varies nicely
  const radiusVariations = [
    1.0, 1.1, 1.15, 1.1, 0.95, 0.85, 0.8, 0.85,
    0.95, 1.05, 1.15, 1.2, 1.15, 1.0, 0.9, 0.85,
    0.9, 1.0, 1.1, 1.05, 0.95, 0.9, 0.95, 1.05,
    1.1, 1.0, 0.9, 0.85, 0.9, 1.0, 1.1, 1.15,
  ];
  
  for (let i = 0; i < numPoints; i++) {
    const angle = (i / numPoints) * Math.PI * 2;
    
    // Interpolate radius variation
    const variationIndex = (i / numPoints) * radiusVariations.length;
    const idx1 = Math.floor(variationIndex) % radiusVariations.length;
    const idx2 = (idx1 + 1) % radiusVariations.length;
    const t = variationIndex - Math.floor(variationIndex);
    const radiusMultiplier = radiusVariations[idx1]! * (1 - t) + radiusVariations[idx2]! * t;
    
    const radius = baseRadius * radiusMultiplier;
    
    points.push({
      x: center.x + Math.cos(angle) * radius,
      y: center.y + Math.sin(angle) * radius,
    });
  }
  
  return points;
}

/**
 * Generate a star shape with smooth transitions between points and valleys.
 */
export function generateStarShape(
  center: Point,
  outerRadius: number,
  innerRadius: number,
  numPoints: number = 5
): Point[] {
  const points: Point[] = [];
  const totalPoints = numPoints * 2;
  
  for (let i = 0; i < totalPoints; i++) {
    // Offset by -90 degrees so first point is at top
    const angle = (i / totalPoints) * Math.PI * 2 - Math.PI / 2;
    const radius = i % 2 === 0 ? outerRadius : innerRadius;
    
    points.push({
      x: center.x + Math.cos(angle) * radius,
      y: center.y + Math.sin(angle) * radius,
    });
  }
  
  return points;
}

/**
 * Generate a heart shape centered at the given point.
 */
export function generateHeartShape(
  center: Point,
  size: number,
  numPoints: number = 64
): Point[] {
  const points: Point[] = [];
  
  for (let i = 0; i < numPoints; i++) {
    const t = (i / numPoints) * Math.PI * 2;
    
    // Parametric heart equation
    const x = 16 * Math.pow(Math.sin(t), 3);
    const y = -(13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t));
    
    // Scale and translate
    points.push({
      x: center.x + (x / 16) * size,
      y: center.y + (y / 16) * size,
    });
  }
  
  return points;
}

/**
 * Generate an ellipse shape.
 */
export function generateEllipseShape(
  center: Point,
  radiusX: number,
  radiusY: number,
  numPoints: number = 64
): Point[] {
  const points: Point[] = [];
  
  for (let i = 0; i < numPoints; i++) {
    // Start from top (-90 degrees)
    const angle = (i / numPoints) * Math.PI * 2 - Math.PI / 2;
    
    points.push({
      x: center.x + Math.cos(angle) * radiusX,
      y: center.y + Math.sin(angle) * radiusY,
    });
  }
  
  return points;
}

/**
 * Generate a flower/petal shape.
 */
export function generateFlowerShape(
  center: Point,
  baseRadius: number,
  petalDepth: number = 0.3,
  numPetals: number = 6,
  numPoints: number = 72
): Point[] {
  const points: Point[] = [];
  
  for (let i = 0; i < numPoints; i++) {
    const angle = (i / numPoints) * Math.PI * 2;
    
    // Create petal effect using sine waves
    const petalAngle = angle * numPetals;
    const radiusMultiplier = 1 + petalDepth * Math.sin(petalAngle);
    const radius = baseRadius * radiusMultiplier;
    
    points.push({
      x: center.x + Math.cos(angle) * radius,
      y: center.y + Math.sin(angle) * radius,
    });
  }
  
  return points;
}

export type DemoShapeType = 'blob' | 'star' | 'heart' | 'ellipse' | 'flower';

/**
 * Get a demo shape by type, scaled appropriately for the viewport.
 */
export function getDemoShape(
  type: DemoShapeType,
  center: Point,
  viewport: { width: number; height: number }
): Point[] {
  // Base radius is roughly 35% of the smaller viewport dimension
  const baseRadius = Math.min(viewport.width, viewport.height) * 0.35;
  
  switch (type) {
    case 'blob':
      return generateBlobShape(center, baseRadius);
    case 'star':
      return generateStarShape(center, baseRadius, baseRadius * 0.45, 5);
    case 'heart':
      return generateHeartShape(center, baseRadius);
    case 'ellipse':
      return generateEllipseShape(center, baseRadius * 1.2, baseRadius * 0.8);
    case 'flower':
      return generateFlowerShape(center, baseRadius, 0.25, 6);
    default:
      return generateBlobShape(center, baseRadius);
  }
}

/** All available demo shape types */
export const DEMO_SHAPE_TYPES: DemoShapeType[] = ['ellipse', 'heart', 'flower'];
